import {Component} from '@angular/core';
import {FormControl, FormGroup, FormRecord, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {DashboardFilter, DashboardSection, SupersetApiService, SupersetDashboardResponse} from '@npck/af-frontend-api';
import {RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {DASHBOARD_ID, DASHBOARD_SECTION} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AnalyticsTab} from '../../analytics.component';
import {sectionToSlug, slugToSection} from '../../dashboard-section';

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const DATASET_IDS_PATTERN = /^\s*\d+(\s*,\s*\d+)*\s*$/;
const RESTRICTIVE_FILTERS = [DashboardFilter.ORGANIZATION, DashboardFilter.GOV_ORGANIZATION];
const MAX_NAME_LENGTH = 255;
const INITIAL_SPINNER = 'initialDashboard';
const SUBMIT_SPINNER = 'submitDashboard';

@Component({
    templateUrl: './dashboard-form.component.html',
    standalone: false,
})
export class DashboardFormComponent {
    Path = Path;
    form: FormGroup<DashboardForm>;
    dashboardId: string;
    section: DashboardSection;
    dashboard?: SupersetDashboardResponse;
    availableFilters = [DashboardFilter.REPORT_DATE, DashboardFilter.ORGANIZATION, DashboardFilter.GOV_ORGANIZATION];
    selectedFilters: DashboardFilter[] = [];
    restrictiveFilters = RESTRICTIVE_FILTERS;
    maxNameLength = MAX_NAME_LENGTH;
    initialSpinner = INITIAL_SPINNER;
    submitSpinner = SUBMIT_SPINNER;
    tabPathToTab: Record<string, AnalyticsTab> = {[Path.INCIDENTS]: 'tabIncidents'};

    constructor(
        private activatedRoute: ActivatedRoute,
        private supersetApiService: SupersetApiService,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
    ) {
        this.dashboardId = this.activatedRoute.snapshot.paramMap.get(DASHBOARD_ID);
        this.section = slugToSection(this.activatedRoute.snapshot.paramMap.get(DASHBOARD_SECTION));
        this.form = new FormGroup<DashboardForm>({
            name: new FormControl('', {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(MAX_NAME_LENGTH)],
            }),
            dashboardId: new FormControl('', {
                nonNullable: true,
                validators: [Validators.required, Validators.pattern(UUID_PATTERN)],
            }),
            roles: new FormControl([], {nonNullable: true, validators: [Validators.required]}),
            filters: new FormControl([], {nonNullable: true}),
            filterDatasets: new FormRecord<FormControl<string>>({}),
        });
        this.form.controls.filters.valueChanges.subscribe(filters => this.syncDatasetControls(filters));
        if (this.dashboardId) {
            Spinner.show(INITIAL_SPINNER);
            this.supersetApiService.getDashboard(this.dashboardId)
                .pipe(rxSpinner(INITIAL_SPINNER))
                .subscribe(dashboard => {
                    this.dashboard = dashboard;
                    this.form.patchValue({
                        name: dashboard.name,
                        dashboardId: dashboard.dashboardId,
                        roles: dashboard.roles,
                        filters: Object.keys(dashboard.filters) as DashboardFilter[],
                    });
                    Object.entries(dashboard.filters).forEach(([filter, datasetIds]) => {
                        this.form.controls.filterDatasets.get(filter)?.setValue(datasetIds.join(', '));
                    });
                });
        }
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        const filters = this.buildFilters();
        const request$ = this.dashboardId
            ? this.supersetApiService.updateDashboard(this.dashboardId, {
                dashboardId: value.dashboardId,
                name: value.name,
                roles: value.roles,
                filters,
            })
            : this.supersetApiService.createDashboard({
                dashboardId: value.dashboardId,
                name: value.name,
                section: this.section,
                roles: value.roles,
                filters,
            });
        request$.pipe(rxSpinner(SUBMIT_SPINNER)).subscribe(dashboard => {
            this.navigateBack(dashboard.id);
            this.snackbarService.showSnackBar(
                this.dashboardId ? 'i18n_af_snackbarMessageDashboardUpdated' : 'i18n_af_snackbarMessageDashboardCreated',
                'success',
                false,
                {name: dashboard.name});
        });
    }

    navigateBack(activeDashboardId?: string): void {
        const path = Path.ANALYTICS_INCIDENTS_DASHBOARDS.replace(`:${DASHBOARD_SECTION}`, sectionToSlug(this.section));
        this.routerService.navigate(path, {
            queryParams: {[TAB]: activeDashboardId ?? this.dashboardId},
        });
    }

    private syncDatasetControls(filters: DashboardFilter[]): void {
        const record = this.form.controls.filterDatasets;
        this.selectedFilters = this.availableFilters.filter(filter => filters.includes(filter));
        Object.keys(record.controls)
            .filter(filter => !this.selectedFilters.includes(filter as DashboardFilter))
            .forEach(filter => record.removeControl(filter));
        this.selectedFilters
            .filter(filter => !record.contains(filter))
            .forEach(filter => record.addControl(filter, new FormControl('', {
                nonNullable: true,
                validators: [Validators.pattern(DATASET_IDS_PATTERN)],
            })));
    }

    private buildFilters(): {[key: string]: number[]} {
        const datasets = this.form.controls.filterDatasets.getRawValue();
        const result: {[key: string]: number[]} = {};
        this.selectedFilters.forEach(filter => {
            result[filter] = (datasets[filter] ?? '').split(',').map(id => id.trim()).filter(id => id).map(Number);
        });
        return result;
    }
}

interface DashboardForm {
    name: FormControl<string>;
    dashboardId: FormControl<string>;
    roles: FormControl<string[]>;
    filters: FormControl<DashboardFilter[]>;
    filterDatasets: FormRecord<FormControl<string>>;
}
